/** The player's heroes, down the right-hand strip.
 *
 * One row per hero: portrait, colour pip, name, level, class. It is a *picker*,
 * and that is all it is.
 *
 * It used to carry a movement gauge and a mana gauge as well. Those two numbers
 * were then stated three times inside a 500px column - as bars here, as gauges
 * on the hero sheet 300px below, and as words on the info plate 300px below
 * that - and three statements of one fact do not make it three times as clear,
 * they make the reader check which one is stale. The hero sheet owns both
 * numbers now, because it is the panel that has room to show them against their
 * ceilings; the roster row shows only what a picker has to show, which is who
 * is on the list and which one is picked.
 *
 * The ~50 device pixels a row gave back went to the army rack, whose sockets
 * were 31x47 with a creature drawn 36px tall inside them.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { ContentRegistry, HeroDefId, HeroId, ObservedHero } from "@game/core";
import {
  BitmapText, measureDrawnBitmapText, romanFaceForStep, useBitmapScale,
} from "../theme/bitmapText.js";
import { Frame, Icon, Panel, ScrollArea, cx } from "../theme/components.js";
import { portraitKey } from "../theme/art.js";
import { METRICS, playerColour } from "../theme/tokens.js";
import { useContent, useObservation } from "../hooks/useGameState.js";
import { formatNumber, gaugeReading } from "./format.js";
import { heroDisplayName } from "./hero-name.js";
import { ownHeroes } from "./select.js";
import type { HeroCapacities, HoverTarget } from "./types.js";

/**
 * Rows the roster draws even when nothing is in them.
 *
 * One, and the one is only ever drawn when the player holds no heroes at all -
 * where it carries "Hire a hero at a tavern", which is an instruction rather
 * than a placeholder. The classic sizes the rack to the heroes you actually have.
 *
 * It was four. On day one, with a single hero, that printed the word EMPTY
 * three times down the panel in letterspaced grey caps, and because the word
 * repeated it became the third most prominent text on the column after the two
 * headings - a debug overlay's tell, on the panel a player looks at most. The
 * 132 device pixels it cost go to the hero sheet, which had nothing but wood in
 * the same band.
 *
 * It is a *display* count and not a rule: a player who holds more heroes than
 * this sees all of them, because the rack grows to the roster rather than
 * clipping it, and the strip scrolls.
 */
export const ROSTER_SLOTS = 1;

export interface HeroListProps {
  readonly selectedHeroId: HeroId | null;
  readonly onSelect: (id: HeroId) => void;
  /** A second press on the already-selected hero opens his sheet; see `HeroRow`. */
  readonly onOpen?: ((id: HeroId) => void) | undefined;
  readonly onHover?: ((target: HoverTarget) => void) | undefined;
  /** Movement / mana ceilings from the host. See `HeroCapacity`. */
  readonly capacities?: HeroCapacities | undefined;
  readonly className?: string | undefined;
}

export function HeroList(props: HeroListProps): JSX.Element {
  const obs = useObservation();
  const content = useContent();
  const heroes = useMemo(() => ownHeroes(obs), [obs]);
  // The roster's names step with the window on the same media step the stat
  // pills and the treasury figures use, so nothing on the column is at a
  // magnification of its own.
  const nameScale = useBitmapScale(2);

  const clearHover = useCallback(() => props.onHover?.({ kind: "NONE" }), [props]);

  return (
    <Panel className={cx("hm-col", "hm-adv-band", props.className)} style={{ minHeight: 0 }}>
      {/* An engraved caption rule, not a title. `hm-title` is the 15px display
          face the hero sheet's name is set in, and spending it plus a panel's
          full padding on the word HEROES made a two-word heading as loud as the
          hero it was heading. See `.hm-adv-band__title`. */}
      <div className="hm-adv-band__title">
        {/* Stamped, like every other caption on this column. It was the kit's
            display serif and the count beside it took the kit's fixed-pitch
            numeral face, so a two-word heading over a bitmap hero name carried
            two typefaces of its own. */}
        <BitmapText face="roman" scale={1}>Heroes</BitmapText>
        <span className="hm-adv-band__count">
          <BitmapText face="roman" scale={1}>{heroes.length}</BitmapText>
        </span>
      </div>
      <ScrollArea className="hm-grow" onPointerLeave={clearHover}>
        <div className="hm-col" style={{ gap: "calc(var(--hm-u) * 2)" }}>
          {heroes.map((hero) => (
            <HeroRow
              key={hero.id}
              hero={hero}
              selected={hero.id === props.selectedHeroId}
              portrait={portraitFor(content, hero.def)}
              name={heroDisplayName(hero, content)}
              heroClass={classOf(content, hero.def)}
              spent={(hero.detail?.movementLeft ?? 0) <= 0}
              colour={playerColour(obs.self?.colour ?? null)}
              nameScale={nameScale}
              onSelect={props.onSelect}
              onOpen={props.onOpen}
              onHover={props.onHover}
            />
          ))}
          {emptySlots(heroes.length).map((i) => (
            <EmptyHeroRow key={`slot-${i}`} hint={heroes.length === 0 && i === 0} />
          ))}
        </div>
      </ScrollArea>
    </Panel>
  );
}

const emptySlots = (filled: number): number[] =>
  Array.from({ length: Math.max(0, ROSTER_SLOTS - filled) }, (_, i) => i);

/**
 * An unused rack slot.
 *
 * A full-width seated plate with a socket at its left, not a socket with a bare
 * hairline running off to the right: the second shape reads as a row whose
 * contents failed to load, which is exactly what a reviewer called it. Every
 * slot carries its own word so the rack says what it is for.
 */
function EmptyHeroRow(props: { readonly hint: boolean }): JSX.Element {
  return (
    <div
      className="hm-adv-rack"
      aria-hidden={!props.hint}
      // The row style carries a pointer cursor and a hover wash for the real
      // rows; an empty slot must not offer either, and there is no hovering an
      // element that takes no pointer events.
      style={{ pointerEvents: "none", cursor: "default" }}
    >
      <span
        className="hm-adv-rack__socket"
        style={{ width: METRICS.portraitSmall, height: METRICS.portraitSmall, display: "block" }}
      />
      <span className="hm-adv-rack__label">
        {props.hint ? "Hire a hero at a tavern" : "Empty"}
      </span>
    </div>
  );
}

function classOf(content: ContentRegistry | null, def: string): string | null {
  const heroDef = content?.heroes.get(def as HeroDefId);
  const name = heroDef?.heroClass;
  return name === undefined || name === "" ? null : name;
}

function portraitFor(content: ContentRegistry | null, def: string): string | null {
  if (content === null) return null;
  const heroDef = content.heroes.get(def as HeroDefId);
  if (heroDef === undefined) return null;
  return portraitKey(heroDef.portrait);
}

function HeroRow(props: {
  readonly hero: ObservedHero;
  readonly selected: boolean;
  readonly portrait: string | null;
  readonly name: string;
  /** The hero's class, where the content bundle has loaded. */
  readonly heroClass: string | null;
  /** True when this hero has no movement left today. */
  readonly spent: boolean;
  readonly colour: string;
  /** Magnification for the stamped name and level tag. */
  readonly nameScale: number;
  readonly onSelect: (id: HeroId) => void;
  readonly onOpen?: ((id: HeroId) => void) | undefined;
  readonly onHover?: ((t: HoverTarget) => void) | undefined;
}): JSX.Element {
  const { hero } = props;

  const open = useCallback(() => props.onOpen?.(hero.id), [props, hero.id]);
  /**
   * Press to centre, press again to open.
   *
   * Not "open if already selected", which is what the town roster does: a hero
   * is SELECTED the moment the map loads, so that rule would throw a
   * full-screen sheet over the board on the very first press - and on a phone
   * the roster is the only way to reach a hero at all, so a stray tap would
   * bury the map. The press that centres is always a press of its own, and the
   * one after it opens.
   *
   * `armed` is cleared whenever the selection moves elsewhere, so coming back
   * to a hero centres on him again rather than opening his sheet from across
   * the map.
   */
  const [armed, setArmed] = useState(false);
  const selected = props.selected;
  useEffect(() => { if (!selected) setArmed(false); }, [selected]);
  const select = useCallback(() => {
    props.onSelect(hero.id);
    if (selected && armed) { setArmed(false); open(); } else setArmed(true);
  }, [props, hero.id, selected, armed, open]);
  const enter = useCallback(() => props.onHover?.({ kind: "HERO", id: hero.id }), [props, hero.id]);

  return (
    <button
      type="button"
      className={cx("hm-entity-row", props.selected && "hm-entity-row--selected")}
      onClick={select}
      onDoubleClick={open}
      onPointerEnter={enter}
      aria-pressed={props.selected}
      title={props.spent
        ? `${props.name} - no movement left today`
        : `${props.name} - press to centre, again to open the sheet`}
    >
      <Frame as="span" well className="hm-adv-portrait" style={{ padding: 0, flex: "0 0 auto", lineHeight: 0 }}>
        {/* 256px source at 32px: an exact eighth. */}
        {props.portrait === null
          ? <span className="hm-icon hm-icon--missing" style={{ width: METRICS.portraitSmall, height: METRICS.portraitSmall }} />
          : <Icon atlasKey={props.portrait} size={METRICS.portraitSmall} />}
      </Frame>

      <span className="hm-col hm-grow" style={{ gap: "calc(var(--hm-u) * 1)" }}>
        {/* Name and level tag are ONE mark now.
         *
         * The row used to set the name in the kit's anti-aliased serif and the
         * `L1` beside it as a bitmap numeral - two type systems six pixels apart
         * on one line, which is the loudest thing left on the column once the
         * sockets are right. The face grew a real lowercase, so the name no
         * longer has to leave it to stay mixed case, and both marks step
         * together on the same magnification instead of the tag being pinned at
         * 1 while the name scaled with the window. */}
        <span className="hm-row hm-clip" style={{ gap: "calc(var(--hm-u) * 3)" }}>
          <span className="hm-pip" style={{ backgroundColor: props.colour }} />
          <BitmapText face={romanFaceForStep(props.nameScale)} scale={1}>{props.name}</BitmapText>
          {/* Pushed to the far end of the row, not set four pixels after the
              name. Both are now the same stamped face at the same magnification,
              and at a word's normal spacing `Jorthas` and `L1` read as one string
              - the re-shot 1920x1080 frame said `AdelaL1`. A tag belongs at the
              edge of the plate it tags. */}
          <span className="hm-num" style={{ opacity: 0.92, marginLeft: "auto", paddingLeft: "calc(var(--hm-u) * 4)" }}>
            <BitmapText face={romanFaceForStep(props.nameScale)} scale={1}>{`L${String(hero.level)}`}</BitmapText>
          </span>
        </span>
        {/* One qualitative mark where two quantitative bars were. "Has this
            hero anything left to do today" is the only thing the picker has to
            answer, and it is the one thing a bar at 100% answered badly. */}
        {/* Stamped. "Thane" in a hard bitmap face with "Artisan" in a soft
            anti-aliased serif nine pixels under it was the loudest of the
            three type systems this column carried. */}
        <span className={cx("hm-adv-roster__state", "hm-clip", props.spent && "hm-adv-roster__state--spent")}>
          <BitmapText face="roman" scale={1} shadow>
            {props.spent ? "No movement left" : props.heroClass ?? "Ready"}
          </BitmapText>
        </span>
      </span>
    </button>
  );
}

/**
 * A movement or mana gauge.
 *
 * Not the kit's `Bar`: two unlabelled flat gradients with a 1px border told the
 * player nothing about what they measured, and the mana one was a cornflower
 * blue that appears nowhere else on a screen of wood and gilt. This is a groove
 * cut into the panel with a lit lip, its own caption cut into the left end and
 * its reading printed at the right end - so the row says "Mov 1,500" rather
 * than showing a gold rectangle and hoping.
 *
 * ## Two things it got wrong, and what settles them
 *
 * **A gauge with no denominator.** Movement and mana both print full on the
 * turn a game starts - 1,600 and 20 - and the bar under them was 100% wide, so
 * the panel showed two solid rectangles and two bare numbers and told the
 * player nothing about either. The classic prints spell points as `20/20`; a reading
 * against its cap is the whole content of a gauge, and without it the bar is
 * decoration. So the default display is `value / max`, and the Experience
 * gauge - which already read `0 / 1,000` - stops being the odd one out.
 *
 * **Two typefaces on one plate.** The reading was a bitmap numeral and the
 * caption six pixels to its left was the kit's anti-aliased serif. Both are set
 * from `glyphs.ts` now, at a magnification the STACK picks by measuring rather
 * than by asking the window how wide it is - a hero at 125,000 of 200,000
 * experience needs a wider reading than one at 0 of 1,000, on the same screen,
 * in the same groove, and no media query can tell those two apart.
 */
export function Gauge(props: {
  readonly kind: "move" | "mana" | "xp";
  readonly label: string;
  readonly value: number;
  readonly max: number;
  /** What to print at the right end, when it is not `value / max`. */
  readonly display?: string | undefined;
  /**
   * The magnification for BOTH marks, chosen by whoever owns the stack.
   *
   * It is not decided here, and that is the point: three gauges sit one under
   * another in the hero sheet, and each one measuring only itself picked its
   * own step - at 1920x1080 Experience and Mana came out at 2 and Movement, one
   * reading wider, at 1, so three plates in a 90px band carried two sizes of the
   * same face. The stack picks one step that fits its widest member and hands
   * it to all three. See `gaugeStackScale`.
   */
  readonly scale?: number | undefined;
  readonly estimated?: boolean | undefined;
}): JSX.Element {
  const max = props.max > 0 ? props.max : 1;
  const pct = Math.max(0, Math.min(100, Math.round((props.value / max) * 100)));
  // Set as authored, not folded to caps. The face carries a real lowercase now,
  // and `Movement` is 43 glyph px against `MOVEMENT`'s 49 - so mixed case is
  // both what the classic stamps and the version that leaves room for the reading.
  const caption = props.label;
  const reading = gaugeReading(props.value, max, props.display);
  const scale = props.scale ?? 1;
  return (
    <span
      className={cx("hm-adv-gauge", `hm-adv-gauge--${props.kind}`)}
      role="meter"
      aria-valuenow={props.value}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-label={props.label}
      title={`${props.label} ${formatNumber(props.value)} of ${formatNumber(max)}`
        + (props.estimated === true ? " (estimated)" : "")}
    >
      {/* The fill is clipped to the groove inside the socket's lip, so a bar at
          100% still has a cut around it. See `.hm-adv-gauge__groove`. */}
      <span className="hm-adv-gauge__groove">
        <span className="hm-adv-gauge__fill" style={{ width: `${pct}%` }} />
      </span>
      <span className="hm-adv-gauge__caption">
        <BitmapText face={romanFaceForStep(scale)} scale={1} title={props.label}>{caption}</BitmapText>
      </span>
      <span className="hm-adv-gauge__value">
        <BitmapText face={romanFaceForStep(scale)} scale={1}>{reading}</BitmapText>
      </span>
    </span>
  );
}

/**
 * What a groove spends on anything that is not a glyph: `--hm-u * 4` of padding
 * at each end and a `--hm-u * 3` gap between the two marks, taken at the widest
 * scale step the kit ships (1.5) and rounded up - 12 + 5.
 *
 * A constant rather than a value read back from the cascade because it is
 * compared against a measured width only to pick a WHOLE magnification, and no
 * plausible slop here can move a whole step. Taking the WIDE step's figure at
 * both viewports errs toward the smaller mark, which clips nothing.
 */
export const GAUGE_CHROME_PX = 17;

/**
 * One magnification for a stack of gauges: the largest whole step at which the
 * widest caption-and-reading pair still fits the groove.
 *
 * `width` is the gauges' own border box - they are full-bleed in their column,
 * so the column's content width is theirs.
 */
export function gaugeStackScale(
  width: number, pairs: readonly (readonly [string, string])[], devicePixelRatio = 1,
): number {
  // Measured as DRAWN - the floor promotes a magnification-1 HUD reading to
  // the display face, which is wider, and a zoomed-out page floors the
  // magnification - so the step that is picked is the step that fits. The
  // pair is the SUM of two marks, not one string: the gap between them is
  // the groove's own (`GAUGE_CHROME_PX` carries it), and measuring
  // `Movement 1,600 / 1,600` with a space glyph inside it charged the gap
  // twice - six texels at magnification 2 - which took a 220px groove down
  // a step it had room for.
  const room = width - GAUGE_CHROME_PX;
  if (room <= 0 || pairs.length === 0) return 1;
  for (let s = 2; s > 1; s--) {
    let widest = 0;
    for (const [caption, reading] of pairs) {
      // The roman family carries its steps as FACES, not as magnifications
      // (see `romanFaceForStep`), so a step is measured in the face it will
      // be stamped in at one device pixel per texel.
      widest = Math.max(widest,
        measureDrawnBitmapText(caption, 1, romanFaceForStep(s), devicePixelRatio).width
        + measureDrawnBitmapText(reading, 1, romanFaceForStep(s), devicePixelRatio).width);
    }
    if (widest <= room) return s;
  }
  return 1;
}
